import moment from "moment";

export const localTimeToUtcHHmm = (date) => moment(date).utc().format("HH:mm");

export const utcHHmmToLocal = (hhmm, refDate = new Date()) => {
  const dateStr = moment(refDate).format("YYYY-MM-DD");
  return moment.utc(`${dateStr}T${hhmm}:00`).local().format("HH:mm");
};

const WEEKDAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const OCCURRENCE_LABELS = {
  1: "1st",
  2: "2nd",
  3: "3rd",
  4: "4th",
  "-1": "last",
};

// nth is 1-4, or -1 when this is the last occurrence of that weekday in the month.
export const getNthWeekdayOfMonth = (date) => {
  const d = new Date(date);
  const day = d.getDate();
  const weekday = d.getDay();
  const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const isLastOccurrence = day + 7 > daysInMonth;

  return {
    nth: isLastOccurrence ? -1 : Math.ceil(day / 7),
    weekday,
  };
};

export const getWeekdayKey = (weekday) => WEEKDAY_KEYS[weekday];

export const getOccurrenceLabel = (nth) => OCCURRENCE_LABELS[nth] || "last";

const MONTH_KEYS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

const ORDERED_WEEKDAY_KEYS = [
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
];

const joinWithConjunction = (items, conjunction) => {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} ${conjunction} ${items[items.length - 1]}`;
};

export const describeRecurrence = (task, t) => {
  const frequency = task?.Frequency;
  const selectedDays = task?.SelectedDays;
  const conjunction = t("listConjunction");

  const weekdayLabel = (day) => t(`fullDaysName.${day}`);
  const monthLabel = (mIdx) => t(`months.${MONTH_KEYS[mIdx]}`);
  const shortMonthLabel = (mIdx) => t(`shortMonths.${MONTH_KEYS[mIdx]}`);

  switch (frequency) {
    case "OneTime": {
      const dateStr = selectedDays?.date;
      if (!dateStr) return "";
      const [year, month, day] = dateStr.split("-").map(Number);
      return t("recurrenceOnceOnDate", {
        day,
        month: shortMonthLabel(month - 1),
        year,
      });
    }

    case "Daily": {
      const storedTimes = Array.isArray(selectedDays?.times) && selectedDays.times.length > 0
        ? selectedDays.times
        : ["08:00"];
      const times = storedTimes.map((hhmm) => utcHHmmToLocal(hhmm)).sort();
      return times.length === 1
        ? t("recurrenceEveryDayAt", { time: times[0] })
        : t("recurrenceEveryDayAtTimes", { times: times.join(", ") });
    }

    case "Weekly": {
      const days = Array.isArray(selectedDays) ? selectedDays : [];
      if (days.length === 0) return "";

      const daySet = new Set(days.map((d) => d.toLowerCase()));
      const isWeekdaySet = daySet.size === 5 &&
        ["monday", "tuesday", "wednesday", "thursday", "friday"].every((d) => daySet.has(d));
      const isEveryDay = daySet.size === 7;

      if (isEveryDay) return t("recurrenceEveryAllDays");
      if (isWeekdaySet) return t("recurrenceEveryWeekday");

      const orderedLabels = ORDERED_WEEKDAY_KEYS
        .filter((key) => daySet.has(key))
        .map(weekdayLabel);

      return orderedLabels.length === 1
        ? t("recurrenceEveryOneDay", { day: orderedLabels[0] })
        : t("recurrenceEveryDays", { days: joinWithConjunction(orderedLabels, conjunction) });
    }

    case "Monthly": {
      const adv = selectedDays || {};
      if (adv.monthlyType === "weekday_occurrence" && Array.isArray(adv.monthlyOccurrences)) {
        const phrases = adv.monthlyOccurrences.map((occ) =>
          t("recurrenceMonthlyOccurrencePhrase", {
            occurrence: t(occ.occurrence),
            weekday: weekdayLabel(occ.weekday),
          })
        );
        return t("recurrenceEveryMonthOn", { desc: joinWithConjunction(phrases, conjunction) });
      }

      const day = adv.monthlyDay ?? task?.MonthlyDay;
      return day === "last"
        ? t("recurrenceEveryMonthLastDay")
        : t("recurrenceEveryMonthOnDay", { day });
    }

    case "Yearly": {
      const adv = selectedDays || {};

      if (adv.yearlyType === "weekday_occurrence" && Array.isArray(adv.yearlyOccurrences)) {
        if (adv.yearlyOccurrences.length === 0) return "";
        const phrases = adv.yearlyOccurrences.map((occ) =>
          t("recurrenceYearlyOccurrencePhrase", {
            occurrence: t(occ.occurrence),
            weekday: weekdayLabel(occ.weekday),
            month: monthLabel(occ.month),
          })
        );
        return t("recurrenceEveryYearOn", { desc: joinWithConjunction(phrases, conjunction) });
      }

      const dates = Array.isArray(adv.dates) ? adv.dates : [];
      if (dates.length === 0) return "";

      const phrases = dates.map((entry) =>
        entry.day === "last"
          ? t("recurrenceYearlyLastDayPhrase", { month: monthLabel(entry.month) })
          : t("recurrenceYearlyDatePhrase", { day: entry.day, month: monthLabel(entry.month) })
      );
      return t("recurrenceEveryYearOn", { desc: joinWithConjunction(phrases, conjunction) });
    }

    default:
      return "";
  }
};

// Counts the actual configured occurrences from a parsed {frequency, selectedDays,
// advancedRecurrence} recurrence object -- used for "{N}x Frequency" labels so they
// reflect real occurrence data instead of the RepeatEvery interval-skip field.
export const getOccurrenceCount = (recurrence) => {
  const frequency = recurrence?.frequency;
  const selectedDays = recurrence?.selectedDays;
  const adv = recurrence?.advancedRecurrence || {};

  switch (frequency) {
    case "Daily":
      return Array.isArray(adv.times) && adv.times.length > 0 ? adv.times.length : 1;

    case "Weekly":
      return Array.isArray(selectedDays) && selectedDays.length > 0 ? selectedDays.length : 1;

    case "Monthly":
      return adv.monthlyType === "weekday_occurrence" && Array.isArray(adv.monthlyOccurrences)
        ? adv.monthlyOccurrences.length || 1
        : 1;

    case "Yearly":
      if (Array.isArray(adv.dates) && adv.dates.length > 0) return adv.dates.length;
      if (adv.yearlyType === "weekday_occurrence" && Array.isArray(adv.yearlyOccurrences)) {
        return adv.yearlyOccurrences.length || 1;
      }
      return 1;

    default:
      return 1;
  }
};
